import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ScienceIcon from '@mui/icons-material/Science';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { useSnackbar } from 'notistack';

import { ConfirmDialog } from '../common';
import Gate from '../access/Gate';
import {
  pushAdminApi,
  PushUserSearchResult,
  UserPushState,
} from '../../services/pushSettingsApi';

const fmt = (n: number | undefined) => (n ?? 0).toLocaleString('fa-IR');

const jalali = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fa-IR', {
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const PLATFORM_FA: Record<string, string> = {
  web: 'مرورگر',
  android: 'اندروید (اپ)',
  ios: 'آیفون (اپ)',
};

/**
 * «مدیریت پوش کاربر» — ابزار پشتیبانی روی صفحهٔ پوش‌نوتیفیکیشن.
 *
 * کاربر را با شمارهٔ موبایل پیدا می‌کنید، وضعیت تحویلش را می‌بینید و سه
 * اکشن دارید: ارسال تستی، بازنشانی تنظیمات به پیش‌فرض، و حذف دستگاه‌ها
 * (که باعث می‌شود هر دستگاه در بازدید بعدی خودش را از نو ثبت کند).
 */
const UserPushManager: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [options, setOptions] = useState<PushUserSearchResult[]>([]);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<PushUserSearchResult | null>(null);

  const [state, setState] = useState<UserPushState | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  // جستجوی debounce شده — هر کلید، یک aggregate روی users نزند.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (query.trim().length < 2) {
      setOptions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      const res = await pushAdminApi.searchUsers(query.trim());
      setOptions(res.success && res.data ? res.data : []);
      setSearching(false);
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const loadState = async (userId: string) => {
    setLoading(true);
    const res = await pushAdminApi.getUserState(userId);
    if (res.success && res.data) setState(res.data);
    else enqueueSnackbar(res.message || 'خطا', { variant: 'error' });
    setLoading(false);
  };

  useEffect(() => {
    setState(null);
    if (selected?._id) void loadState(selected._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?._id]);

  const runAction = async (
    key: string,
    action: () => Promise<{ success: boolean; message?: string }>,
  ) => {
    if (!selected) return;
    setBusy(key);
    const res = await action();
    setBusy(null);
    enqueueSnackbar(res.message || (res.success ? 'انجام شد' : 'خطا'), {
      variant: res.success ? 'success' : 'error',
    });
    if (res.success) void loadState(selected._id);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <PersonSearchIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={800}>
          مدیریت پوش کاربر
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        برای کاربری که می‌گوید «اعلان برایم نمی‌آید»: وضعیتش را ببینید، تنظیماتش را
        بازنشانی کنید یا دستگاه‌هایش را پاک کنید تا از نو ثبت شوند.
      </Typography>

      <Autocomplete
        options={options}
        value={selected}
        loading={searching}
        filterOptions={(x) => x}
        noOptionsText={query.trim().length < 2 ? 'شمارهٔ موبایل یا نام را بنویسید…' : 'کاربری پیدا نشد'}
        loadingText="در حال جستجو…"
        getOptionLabel={(o) => `${o.name || 'بدون نام'}${o.phoneNumber ? ` — ${o.phoneNumber}` : ''}`}
        isOptionEqualToValue={(a, b) => a._id === b._id}
        onInputChange={(_e, v) => setQuery(v)}
        onChange={(_e, v) => setSelected(v)}
        renderOption={(props, option) => (
          <li {...props} key={option._id}>
            <Stack>
              <Typography variant="body2">{option.name || 'بدون نام'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[option.phoneNumber, option.email].filter(Boolean).join(' • ') || '—'}
              </Typography>
            </Stack>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} size="small" label="جستجوی کاربر" placeholder="۰۹۱۲۳۴۵۶۷۸۹ یا نام" />
        )}
      />

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="caption">در حال دریافت وضعیت…</Typography>
        </Stack>
      )}

      {state && !loading && (
        <Box sx={{ mt: 2 }}>
          {/* یک‌خطیِ وضعیت */}
          <Alert
            severity={state.summary.canReceivePush ? 'success' : 'warning'}
            icon={
              state.summary.canReceivePush ? (
                <NotificationsActiveIcon fontSize="small" />
              ) : (
                <NotificationsOffIcon fontSize="small" />
              )
            }
            sx={{ mb: 1.5 }}
          >
            {state.summary.canReceivePush
              ? `این کاربر می‌تواند پوش بگیرد — ${fmt(state.summary.activeDevices)} دستگاه فعال.`
              : 'هیچ دستگاه فعالی ثبت نیست — کاربر باید یک بار سایت/اپ را باز کند، یا از صفحهٔ «تنظیمات اعلان‌ها»ی خودش «اعمال مجدد» بزند.'}
          </Alert>

          {/* چیپ‌های خلاصه */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Chip size="small" label={`دستگاه فعال: ${fmt(state.summary.activeDevices)}`} />
            <Chip size="small" variant="outlined" label={`غیرفعال: ${fmt(state.summary.inactiveDevices)}`} />
            <Chip size="small" variant="outlined" label={`توکن قدیمی: ${fmt(state.summary.legacyTokens)}`} />
            <Chip
              size="small"
              color={state.preferences.pushEnabled ? 'success' : 'warning'}
              variant="outlined"
              label={state.preferences.pushEnabled ? 'پوش: روشن' : 'پوش: خاموش (توسط کاربر)'}
            />
            {state.preferences.quietHours.enabled && (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={`ساعات سکوت ${fmt(state.preferences.quietHours.startHour)}–${fmt(state.preferences.quietHours.endHour)}`}
              />
            )}
            {state.preferences.mutedCategories.length > 0 && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`${fmt(state.preferences.mutedCategories.length)} دستهٔ بی‌صدا`}
              />
            )}
            {!state.preferences.isCustomized && (
              <Chip size="small" variant="outlined" label="تنظیمات: پیش‌فرض" />
            )}
            {state.user.isBanned && <Chip size="small" color="error" label="کاربر مسدود است" />}
          </Stack>

          {/* دستگاه‌ها */}
          {state.devices.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1.5 }}>
              <Stack divider={<Divider />}>
                {state.devices.map((d, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ py: 0.75 }}
                  >
                    <Typography variant="caption" fontWeight={700}>
                      {PLATFORM_FA[d.platform] || d.platform}
                      {d.deviceModel ? ` — ${d.deviceModel}` : ''}
                      {d.isActive === false && (
                        <Typography component="span" variant="caption" sx={{ color: theme.palette.error.main }}>
                          {' '}
                          (غیرفعال)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {jalali(d.lastSeenAt)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}

          {/* اکشن‌ها */}
          <Gate permission="admin_regular_user_push_manage">
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="contained"
                startIcon={busy === 'test' ? <CircularProgress size={14} color="inherit" /> : <ScienceIcon />}
                disabled={busy !== null || !state.summary.canReceivePush}
                onClick={() => void runAction('test', () => pushAdminApi.sendTest(selected!._id))}
              >
                ارسال اعلان تستی
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={busy === 'reset' ? <CircularProgress size={14} /> : <RestartAltIcon />}
                disabled={busy !== null || !state.preferences.isCustomized}
                onClick={() =>
                  void runAction('reset', () => pushAdminApi.resetPreferences(selected!._id))
                }
              >
                بازنشانی تنظیمات به پیش‌فرض
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={busy === 'clear' ? <CircularProgress size={14} /> : <DeleteSweepIcon />}
                disabled={busy !== null || (state.devices.length === 0 && state.summary.legacyTokens === 0)}
                onClick={() => setConfirmClear(true)}
              >
                حذف دستگاه‌ها (ثبت مجدد)
              </Button>
            </Stack>
          </Gate>
        </Box>
      )}

      <ConfirmDialog
        open={confirmClear}
        title="حذف دستگاه‌های کاربر"
        message={
          'همهٔ دستگاه‌های ثبت‌شدهٔ این کاربر حذف می‌شوند و تا باز شدن دوبارهٔ ' +
          'سایت/اپ روی هر دستگاه، پوش دریافت نمی‌کند. سپس هر دستگاه خودکار و ' +
          'تمیز از نو ثبت می‌شود. ادامه می‌دهید؟'
        }
        destructive
        onConfirm={() => {
          setConfirmClear(false);
          void runAction('clear', () => pushAdminApi.clearDevices(selected!._id));
        }}
        onCancel={() => setConfirmClear(false)}
      />
    </Paper>
  );
};

export default UserPushManager;
